/** Typed fetchers over the workflows endpoints — the hooks' only I/O. */
import { apiRequest } from '../../lib/apiClient.js';
import { agentBaseUrl } from '../../lib/agentApi.js';
import type {
  BuilderSchedule,
  BuilderDefinition,
  TestRunResponse,
  NotificationChannelListResponse,
  UserListResponse,
  PipelineEventListResponse,
  WorkflowRunResponse,
  WorkflowsOverviewResponse,
  WorkflowActivityRow,
} from '../../lib/types.js';

const BASE = '/api/workflows';

export const fetchOverview = () => apiRequest<WorkflowsOverviewResponse>(BASE);

export const setWorkflowEnabled = (workflowId: string, isEnabled: boolean) =>
  apiRequest<void>(`${BASE}/${workflowId}/enabled`, {
    method: 'PATCH',
    body: { isEnabled },
  });

export const updateSystemConfig = (workflowId: string, config: unknown) =>
  apiRequest<void>(`${BASE}/${workflowId}/config`, { method: 'PATCH', body: { config } });

export const createComposed = (body: {
  name: string;
  trigger: string;
  definition: BuilderDefinition;
  /** Required when trigger is 'schedule'; the server mints the real key. */
  schedule?: BuilderSchedule;
}) => apiRequest<{ id: string }>(`${BASE}/composed`, { method: 'POST', body });

export const updateComposed = (
  workflowId: string,
  body: {
    name?: string;
    trigger?: string;
    definition?: BuilderDefinition;
    schedule?: BuilderSchedule;
  },
) => apiRequest<void>(`${BASE}/composed/${workflowId}`, { method: 'PATCH', body });

export const deleteComposed = (workflowId: string) =>
  apiRequest<void>(`${BASE}/composed/${workflowId}`, { method: 'DELETE' });

/** Destinations a message can go to, plus the people we could DM. */
export const fetchChannels = () =>
  apiRequest<NotificationChannelListResponse>(`/api/notifications/channels`);

export const fetchUsers = () => apiRequest<UserListResponse>(`/api/users`);

/** Reuses this person's destination, or makes one so they can be messaged.
 * No transport named: how to reach people is tenant policy — the server
 * resolves the medium (Teams DM today). */
/** The tenant-wide Activity feed — live runs across every workflow. */
export const fetchActivity = (status?: string) =>
  apiRequest<WorkflowActivityRow[]>(
    `${BASE}/activity${status ? `?status=${encodeURIComponent(status)}` : ''}`,
  );

export const fetchRuns = (workflowId: string) =>
  apiRequest<WorkflowRunResponse[]>(`${BASE}/composed/${workflowId}/runs`);

/** Test runs live on the ORCHESTRATION deployable — it owns the runner. */
export const createTestRun = (body: {
  workflowId: string;
  definition: BuilderDefinition;
  /** Omitted = "Run now" on a scheduled workflow: the server synthesizes the
   * occurrence item the ticker would enqueue. */
  eventId?: string;
}) =>
  apiRequest<{ runId: string }>('/api/workflow-test-runs', {
    method: 'POST',
    body,
    baseUrl: agentBaseUrl,
  });

export const fetchTestRun = (runId: string) =>
  apiRequest<TestRunResponse>(`/api/workflow-test-runs/${runId}`, { baseUrl: agentBaseUrl });

/** Recent real events of the trigger type — the test rail's event picker. */
export const fetchRecentEvents = (type: string) =>
  apiRequest<PipelineEventListResponse>(
    `/api/events?view=all&type=${encodeURIComponent(type)}&limit=10`,
  );
